import { MessageCircle, Smartphone, Image, FileText, RefreshCw, MonitorPlay } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";

const BOT_USERNAME = "@digisingn_admin_bot";
const TELEGRAM_LINK = `https://t.me/digisingn_admin_bot`;

function StepCard({ number, title, children, icon: Icon }) {
  return (
    <div className="bg-white rounded-lg border border-zinc-200 p-5 flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-sm font-bold">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          {Icon && <Icon className="h-4 w-4 text-[#002FA7]" />}
          <h3 className="font-semibold text-zinc-800">{title}</h3>
        </div>
        <div className="text-sm text-zinc-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function ExampleCard({ command, description }) {
  return (
    <div className="bg-zinc-50 rounded-lg border border-zinc-200 p-3 flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-zinc-800">{command}</p>
        <p className="text-xs text-zinc-500">{description}</p>
      </div>
    </div>
  );
}

export default function BotAssistant() {
  const { user } = useAuth();

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 bg-[#002FA7] rounded-lg flex items-center justify-center">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Bot Assistant</h1>
            <p className="text-sm text-zinc-500">
              Kelola signage lewat Telegram — upload, pasang, dan refresh layar dari chat
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <a
        href={TELEGRAM_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="block mb-8 bg-[#002FA7] hover:bg-[#001F7A] text-white rounded-lg p-5 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold mb-1">Buka Bot di Telegram</h2>
            <p className="text-sm text-white/80">Klik untuk langsung chat dengan {BOT_USERNAME}</p>
          </div>
          <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
            <Smartphone className="h-6 w-6" />
          </div>
        </div>
      </a>

      {/* Step-by-step */}
      <h2 className="text-lg font-bold text-zinc-800 mb-4">Cara Pakai</h2>
      <div className="space-y-4 mb-8">
        <StepCard number={1} title="Buka Telegram & Cari Bot" icon={Smartphone}>
          Buka aplikasi Telegram, cari <strong>{BOT_USERNAME}</strong> atau klik tombol di atas. Lalu tekan <strong>Start</strong>.
        </StepCard>

        <StepCard number={2} title="Login dengan Akun DigiSign" icon={MessageCircle}>
          Ketik perintah berikut di chat bot:
          <div className="mt-2 bg-zinc-900 text-green-400 text-xs font-mono p-2 rounded">
            /login email_anda@email.com password_anda
          </div>
          <p className="mt-1 text-xs text-zinc-400">
            Gunakan email & password yang biasa dipakai login ke web DigiSign.
          </p>
        </StepCard>

        <StepCard number={3} title="Mulai Chat Natural" icon={FileText}>
          Setelah login, langsung chat pakai bahasa sehari-hari. Bot akan otomatis paham maksud Anda.
        </StepCard>
      </div>

      {/* Contoh Perintah */}
      <h2 className="text-lg font-bold text-zinc-800 mb-4">Contoh Perintah</h2>
      <div className="space-y-2 mb-8">
        <ExampleCard
          command="cek status device"
          description="Lihat status semua layar (online/offline)"
        />
        <ExampleCard
          command="refresh layar lobby"
          description="Refresh satu device tertentu"
        />
        <ExampleCard
          command="refresh semua layar"
          description="Refresh semua device"
        />
        <ExampleCard
          command="tampilkan jadwal"
          description="Lihat daftar jadwal yang ada"
        />
      </div>

      {/* Upload Media */}
      <h2 className="text-lg font-bold text-zinc-800 mb-4">Upload & Pasang ke Layar</h2>
      <div className="space-y-2 mb-8">
        <ExampleCard
          command="(kirim foto/video) + caption: tampilkan di lobby"
          description="Upload & langsung pasang di device tertentu"
        />
        <ExampleCard
          command="(kirim foto/video) + caption: tampilkan di semua layar"
          description="Upload & tampilkan di semua device"
        />
        <ExampleCard
          command="ganti running text jadi Selamat Datang"
          description="Ubah teks berjalan di layout signage"
        />
      </div>

      {/* Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <p className="font-semibold mb-1">📌 Catatan</p>
        <ul className="list-disc list-inside space-y-1 text-amber-700">
          <li>Bot hanya bisa digunakan oleh admin (role admin / property_admin)</li>
          <li>Setiap user hanya bisa mengelola properti mereka sendiri</li>
          <li>File yang diupload otomatis tersimpan di Media Library</li>
          <li>Pastikan device sudah terhubung (online) agar refresh berhasil</li>
        </ul>
      </div>
    </div>
  );
}
